// Pure data assembly for the standalone-web target. Takes the JSON payload from the /api/data Worker
// (each table = array of { id, fields:{ fieldId: value } }) and runs the SAME joinCore builders the
// Airtable extension's useParcels hook uses, so parcels/dock are identical to the extension.
//
// Canonical record shape (what the Worker MUST emit so this matches the SDK):
//   • link fields     → [{ id, name }]  (the Worker resolves REST record-ids → primary-field names;
//                        the SDK returns the same, so getCellValue can join by .id AND stringify .name)
//   • lookup fields   → flat array of looked-up values (REST passthrough; num()/sStr() handle arrays)
//   • single/multi select → { id, name } / [{ id, name }]
//   • everything else → the raw scalar (text/number/checkbox/date-ISO/attachment[])
// Both useParcelsApi (the React hook) and scripts/verify-web-data.mjs call assembleParcels(), so they
// can never diverge — the verify script IS the contract test for this function.
import { buildUpgradesByUnit, buildParcels, buildDockData, buildSyncedAt, num, sStr } from './joinCore';
import type { Parcel, DockData } from './joinCore';
import { UPGRADE } from '../ids';

export interface MissingField { id: string; label: string }
export interface RawRec { id: string; fields: Record<string, any> }
export interface ApiPayload {
  lots?: RawRec[]; units?: RawRec[]; permits?: RawRec[]; inspections?: RawRec[];
  sales?: RawRec[]; punch?: RawRec[]; lotUpdates?: RawRec[]; changeLog?: RawRec[];
  upgrades?: RawRec[]; models?: RawRec[]; drawings?: RawRec[];
  fieldNames?: Record<string, string>;
}
export type AssembledData = { parcels: Parcel[]; fieldNames: Record<string, string>;
  missingFields: MissingField[]; syncedAt: { permits: number | null; sales: number | null } } & DockData;

/** getCellValueAsString equivalent: render any cell value to the SDK's display string closely enough
 *  for joinCore (which reads strings via sStr and numbers/ids via num/cv). Handles the canonical
 *  shapes above plus the MCP lookup wrapper so the verify harness (MCP data) exercises the same path. */
export function stringifyCell(v: any): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'checked' : '';
  if (Array.isArray(v)) return v.map(stringifyCell).filter((s) => s !== '').join(', ');
  if (typeof v === 'object') {
    if ((v as any).valuesByLinkedRecordId) { // MCP lookup wrapper → flatten (verify path)
      const out: string[] = [];
      for (const arr of Object.values((v as any).valuesByLinkedRecordId)) for (const x of (arr as any[]) || []) { const s = stringifyCell(x); if (s) out.push(s); }
      return out.join(', ');
    }
    if ((v as any).name != null) return String((v as any).name);
    if ((v as any).filename != null) return String((v as any).filename);
    if ((v as any).text != null) return String((v as any).text);
    if ((v as any).value != null) return stringifyCell((v as any).value);
    if (Array.isArray((v as any).linkedRecordIds)) return (v as any).linkedRecordIds.join(', ');
    return '';
  }
  return String(v);
}

/** Wrap a raw REST record as the { id, getCellValue, getCellValueAsString } shape joinCore consumes. */
export function wrapRecord(r: RawRec): any {
  const f = r.fields || {};
  return { id: r.id, getCellValue: (fid: string) => (fid in f ? f[fid] : null), getCellValueAsString: (fid: string) => stringifyCell(f[fid]) };
}
const wrapAll = (rs?: RawRec[]) => (rs || []).map(wrapRecord);

/** Run the full extension assembly (mirrors useParcels.ts lines ~182–279) over a fetched payload. */
export function assembleParcels(payload: ApiPayload): AssembledData {
  const lots = wrapAll(payload.lots), permits = wrapAll(payload.permits), units = wrapAll(payload.units),
    insps = wrapAll(payload.inspections), sales = wrapAll(payload.sales), punch = wrapAll(payload.punch),
    lotUpdates = wrapAll(payload.lotUpdates), changeLog = wrapAll(payload.changeLog),
    upgrades = wrapAll(payload.upgrades), models = wrapAll(payload.models), drawings = wrapAll(payload.drawings);

  const upgradesByUnit = buildUpgradesByUnit(units);
  const dock = buildDockData({ lots, permits, units, insps, sales, punch, lotUpdates, changeLog });

  // Per-unit sale-derived maps off the WINNING sale (dock.salesByUnit) — same as useParcels.
  const fin = new Map<string, string>(), job = new Map<string, string>(), add = new Map<string, string>(),
    esc = new Map<string, string>(), escRecv = new Map<string, string>();
  const sbu = dock.salesByUnit;
  if (sbu) for (const [unitId, s] of sbu) {
    if (s.financialIncentives) fin.set(unitId, s.financialIncentives);
    if (s.jobContingency) job.set(unitId, s.jobContingency);
    if (s.additionalContingencies) add.set(unitId, s.additionalContingencies);
    if (s.escrowStatus) esc.set(unitId, s.escrowStatus);
    if (s.escrowReceivedDate) escRecv.set(unitId, s.escrowReceivedDate);
  }
  const upgradePricing = new Map<string, number>();
  for (const r of upgrades) {
    if (!sStr(r, UPGRADE.funnel).includes('Construction Upgrades')) continue;
    const amount = num(r, UPGRADE.amount); if (amount == null) continue;
    const aliases = sStr(r, UPGRADE.ghlAliases).split('\n').map((s) => s.trim()).filter(Boolean); if (!aliases.length) continue;
    const ms = sStr(r, UPGRADE.appliesToModels).split(',').map((s) => s.trim()).filter(Boolean);
    for (const m of ms) for (const a of aliases) upgradePricing.set(`${m}|${a}`, amount);
  }
  const creditsByUnit = new Map<string, { upgradeCredit: number; pctOff: number; baseCredit: number }>();
  if (sbu) for (const [unitId, s] of sbu) creditsByUnit.set(unitId, { upgradeCredit: s.upgradeCredit ?? 0, pctOff: s.upgradePctOff ?? 0, baseCredit: s.baseCredit ?? 0 });
  const basePriceByUnit = new Map<string, number>();
  if (sbu) for (const [unitId, s] of sbu) if (s.basePurchasePrice != null) basePriceByUnit.set(unitId, s.basePurchasePrice);

  const parcels = buildParcels({ lots, permits, units, insps, upgrades, models, drawings, upgradesByUnit,
    financialIncentivesByUnit: fin, jobContingencyByUnit: job, additionalContingenciesByUnit: add,
    escrowStatusByUnit: esc, escrowReceivedByUnit: escRecv, upgradePricing, creditsByUnit, basePriceByUnit });

  const syncedAt = buildSyncedAt(permits, sales);
  let unitRows = dock.unitRows;
  if (dock.unitRows.length && parcels.length) {
    const priceByLotId = new Map(parcels.map((p) => [p.id, p.priceBreakdown.finalSalesPrice]));
    unitRows = dock.unitRows.map((ur) => {
      if (ur.finalSalesPrice != null) return ur;
      const price = ur.lotId ? priceByLotId.get(ur.lotId) : undefined;
      return price != null ? { ...ur, finalSalesPrice: price } : ur;
    });
  }
  return { parcels, ...dock, unitRows, fieldNames: payload.fieldNames || {}, missingFields: [], syncedAt };
}
